/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, {
  useState, useMemo, useEffect, useContext
} from 'react';
import { darkModeAware } from '@glu/theming';
import { withInputAPI, FormContext } from '@glu/validation-react';
import locale from '@glu/locale';
import Error from '../Error/Error';
import useStyles from './NumberInput.styles';
import LabelText from '../LabelText/LabelText';
import InputComponent from '../Input/InputComponent';

let uniqueId = 0;

const NumberInput = ({
  className,
  dark,
  disabled,
  error,
  errors,
  htmlId,
  inline,
  inputClass,
  labelSpanClass,
  labelText,
  max,
  min,
  onBlur,
  onChange,
  optional,
  screenReaderLabel,
  step,
  value,
  ...props
}) => {
  const [internalError, setInternalError] = useState();
  const errorMessage = internalError || error || errors?.[0];
  const classes = useStyles({ disabled, errorMessage });
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `number-input-${uniqueId++}`, [htmlId]);
  const aboveOrEqualMin = (val) => (
    min || min === 0 ? min <= val : true
  );
  const belowOrEqualMax = (val) => (
    max || max === 0 ? max >= val : true
  );
  const getStep = () => (
    // eslint-disable-next-line no-restricted-globals
    isNaN(step) ? 1 : step
  );

  const getValue = () => (
    value ? parseFloat(value) : min || 0
  );

  const updateValue = (val) => {
    let newValue = parseFloat(val);
    if (Number.isNaN(newValue)) {
      // Replace isNaN values with empty string for form validation
      // Fixes warning: `value` prop on `input` should not be null.
      newValue = '';
    }
    onChange(props.name, newValue);
  };

  const formValue = useContext(FormContext);

  const validate = () => {
    const internalErrors = [];
    if (min && !max && !aboveOrEqualMin(value)) {
      internalErrors.push(locale.get('numberAboveMin', min));
    }
    if (!min && max && !belowOrEqualMax(value)) {
      internalErrors.push(locale.get('numberBelowMax', max));
    }
    if (min && max && (!aboveOrEqualMin(value) || !belowOrEqualMax(value))) {
      internalErrors.push(locale.get('numberBetween', min, max));
    }
    setInternalError(internalErrors.length > 0 ? internalErrors[0] : undefined);
    return {
      data: value,
      errors: internalErrors,
      isValid: internalErrors.length === 0
    };
  };

  const handleBlur = (e) => {
    onBlur?.(e);
    validate();
  };

  const handleMinusClick = () => {
    const curValue = getValue(value);
    const newValue = (curValue - getStep()).toPrecision(10);
    updateValue(newValue < min ? min : newValue);
  };

  useEffect(() => formValue?.registerSelfValidatingChild(props.name, validate),
    [formValue]);

  return (
    <label htmlFor={id} className={`${className} ${classes.root} ${inline ? classes.inline : ''}`}>
      <LabelText
        className={`${labelSpanClass} ${classes.label}`}
        screenReaderLabel={screenReaderLabel}
        label={labelText}
        hasError={!!errorMessage}
        optional={optional}
      />
      <div className={classes.number}>
        <button
          className={`${classes.btnCtrl} ${classes.btnMinus}`}
          data-qa="btnDown"
          type="button"
          onClick={handleMinusClick}
          onBlur={handleBlur}
          disabled={disabled || (min && value && min >= value)}
        >
          -
        </button>
        <InputComponent
          id={id}
          {...props}
          className={`${inputClass} ${classes.input} `}
          type="number"
          min={min}
          max={max}
          value={(value || value === 0) ? value : ''}
          step={step}
          disableInputAPI
          error={errorMessage}
          inline
          disabled={disabled}
          onChange={(e) => updateValue(e?.target?.value)}
          onBlur={handleBlur}
        />
        <button
          className={`${classes.btnCtrl} ${classes.btnPlus}`}
          data-qa="btnUp"
          type="button"
          onClick={() => updateValue((getValue() + getStep()).toPrecision(10))}
          onBlur={handleBlur}
          disabled={disabled || (max && value && max <= value)}
        >
          +
        </button>

      </div>
      <Error error={errorMessage} dark={dark} />
    </label>
  );
};

NumberInput.propTypes = {

  /** Custom classes applied to label wrapping component */
  className: PropTypes.string,

  /** dark mode boolean */
  dark: PropTypes.bool,

  /** Disables input field and increment/decrement buttons */
  disabled: PropTypes.bool,

  /** The error to display */
  error: PropTypes.string,

  /** Full errors object. */
  errors: PropTypes.arrayOf(PropTypes.string),

  /** Id for the input if a randomly generated id isn't desired */
  htmlId: PropTypes.string,

  /** Boolean to indicate whether or not you want the standard inline layout */
  inline: PropTypes.bool,

  /** Custom classes applied to input element */
  inputClass: PropTypes.string,

  /** Custom classes applied to label span element */
  labelSpanClass: PropTypes.string,

  /** Text for the span inside the label */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,

  /** Number value that the input cannot exceed */
  max: PropTypes.number,

  /** Number value that the input cannot go below */
  min: PropTypes.number,

  /** Name attribute */
  name: PropTypes.string.isRequired,

  /** Callback method on blur of value */
  onBlur: PropTypes.func,

  /** Callback method on change of value */
  onChange: PropTypes.func,

  /** Shows optional text on label */
  optional: PropTypes.bool,

  /** Boolean controlling whether the labelText should only be shown to screen readers */
  screenReaderLabel: PropTypes.bool,

  /** Value to increment with, can by a number or string 'any' */
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** Theme for styling */
  theme: PropTypes.objectOf(PropTypes.any),

  /** Number value of module */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

NumberInput.defaultProps = {
  className: '',
  dark: false,
  disabled: false,
  error: '',
  errors: [],
  htmlId: null,
  inline: false,
  inputClass: '',
  labelSpanClass: '',
  max: null,
  min: null,
  onBlur: /* istanbul ignore next */ () => {},
  onChange: /* istanbul ignore next */ () => {},
  optional: false,
  screenReaderLabel: false,
  step: 1,
  theme: undefined,
  value: null
};

const StyledNumber = darkModeAware(NumberInput);
const NumberWithInputApi = withInputAPI(StyledNumber);
NumberWithInputApi.displayName = 'Number';
export default NumberWithInputApi;
export const ValidatedNumberInput = NumberWithInputApi;
export const BaseNumberInput = NumberInput;
